import { onMounted, ref } from "vue";

export type ThemePreference = "system" | "light" | "dark";

const STORAGE_KEY = "chuy.tema";

/**
 * El tema.
 *
 * **Claro de fábrica**, y a propósito: Chuy se ve en un teléfono, a media obra,
 * con sol encima. Seguir a la máquina sonaba elegante y en la práctica quería
 * decir que a quien trae el celular en oscuro le tocaba un tablero oscuro que
 * nunca pidió. Quien lo quiera oscuro lo prende, y se le respeta para siempre.
 *
 * El valor de arranque tiene que empatar con el `data-theme="light"` que trae el
 * `index.html`: ese atributo está escrito ahí, y no aquí, para que el tablero
 * nazca claro desde el primer pintado. Puesto solo por JavaScript, un teléfono
 * en modo oscuro alcanzaba a enseñar un parpadeo negro antes de que Vue
 * despertara.
 */
const preference = ref<ThemePreference>("light");

/**
 * El color de la barra de estado cuando Chuy corre como app.
 *
 * Se mueve con el tema y no con el del sistema. Antes eran dos `<meta>` con
 * `prefers-color-scheme`, y con el tema clavado en claro eso dejaba a quien
 * trae el teléfono en oscuro con un tablero claro y una barra de arriba negra.
 */
const COLOR_DE_BARRA: Record<"light" | "dark", string> = {
  light: "#f4f1ef",
  dark: "#0b0d0c",
};

function pintarLaBarra(tema: "light" | "dark"): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = COLOR_DE_BARRA[tema];
}

/** Cuál de los dos está pintando ahorita, ya resuelto el «como la máquina». */
function temaEfectivo(value: ThemePreference): "light" | "dark" {
  if (value !== "system") return value;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function useTheme() {
  function apply(value: ThemePreference): void {
    preference.value = value;
    const root = document.documentElement;
    if (value === "system") {
      root.removeAttribute("data-theme");
    } else {
      root.setAttribute("data-theme", value);
    }
    pintarLaBarra(temaEfectivo(value));
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Navegación privada: la preferencia simplemente no se guarda.
    }
  }

  onMounted(() => {
    let guardado: string | null = null;
    try {
      guardado = localStorage.getItem(STORAGE_KEY);
    } catch {
      // Si el almacenamiento falla, ni modo: se queda con el claro de fábrica.
    }

    // Sin nada guardado no se llama a `apply`: el `index.html` ya dejó puesto el
    // claro, y volver a escribirlo nada más serviría para guardar una
    // preferencia que la persona nunca escogió.
    if (guardado === "light" || guardado === "dark" || guardado === "system") {
      apply(guardado);
    } else {
      pintarLaBarra("light");
    }
  });

  return { preference, apply };
}
